import { apiFetch, isApiMode, TENANT_HEADER } from './http';

// ─────────────────────────────────────────────────────────────────────
// A CENTRE ARRANGES ACTIVITIES IN A CLUB'S NAME — the client (slice 4d,
// 2026-10-21).
//
// `/link/activities` is scoped to the CLUB: every call names it in the tenant
// header, overriding the centre the portal is acting in. The centre the
// activity is arranged by is the server's — from the link the evaluator
// found — and named here only to pick between several, with `?home=`. What
// the page shows is the server's: only the centre's own activities in the
// club (§4k 1A), and who signed up by name (2A).
// ─────────────────────────────────────────────────────────────────────

export interface LinkActivitySignup {
  name: string;
  partner?: string;
  status: string;
}

export interface LinkActivity {
  id: string;
  status: string;
  name: string;
  kind: 'arrangement' | 'coaching' | string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  maxParticipants: number | null;
  description: string | null;
  signups: LinkActivitySignup[];
}

export interface LinkActivities {
  host: { id: string; name: string };
  home: { id: string; name: string };
  activities: LinkActivity[];
}

export interface LinkActivityInput {
  name: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  maxParticipants: number | null;
  description: string | null;
  kind: 'arrangement' | 'coaching';
}

export type LinkActivitiesRead =
  | { state: 'no-api' }
  | { state: 'ok'; value: LinkActivities }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

export type LinkActivityWrite =
  | { state: 'ok' }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

const homeQuery = (home: string | null) => (home ? `?home=${encodeURIComponent(home)}` : '');

/** The centre's own activities in this club, with who signed up. */
export async function fetchLinkActivities(
  hostId: string,
  home: string | null = null,
): Promise<LinkActivitiesRead> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/link/activities${homeQuery(home)}`, {
      headers: { [TENANT_HEADER]: hostId },
    });
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (res.ok) return { state: 'ok', value: body as unknown as LinkActivities };
    const reason = typeof body?.error === 'string' ? body.error : null;
    if (res.status === 403 && reason === 'link_not_active') return { state: 'refused', reason };
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 409)
      return { state: 'refused', reason: reason ?? 'refused' };
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

async function write(path: string, hostId: string, init: RequestInit): Promise<LinkActivityWrite> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, {
      ...init,
      headers: {
        [TENANT_HEADER]: hostId,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    if (res.ok) return { state: 'ok' };
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    const reason = typeof body?.error === 'string' ? body.error : null;
    if (
      res.status === 403 &&
      (reason === 'link_not_active' || reason === 'reauthentication_required')
    ) {
      return { state: 'refused', reason };
    }
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      return { state: 'refused', reason: reason ?? 'refused' };
    }
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: null };
  }
}

/** Arrange a new activity in the club's name. */
export function createLinkActivity(
  hostId: string,
  input: LinkActivityInput,
  home: string | null = null,
): Promise<LinkActivityWrite> {
  return write(`/link/activities${homeQuery(home)}`, hostId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Change one of the centre's own activities in the club. */
export function updateLinkActivity(
  hostId: string,
  id: string,
  change: Partial<LinkActivityInput>,
  home: string | null = null,
): Promise<LinkActivityWrite> {
  return write(`/link/activities/${encodeURIComponent(id)}${homeQuery(home)}`, hostId, {
    method: 'PATCH',
    body: JSON.stringify(change),
  });
}

/** Cancel one of the centre's own activities in the club. */
export function cancelLinkActivity(
  hostId: string,
  id: string,
  home: string | null = null,
): Promise<LinkActivityWrite> {
  return write(`/link/activities/${encodeURIComponent(id)}/cancel${homeQuery(home)}`, hostId, {
    method: 'POST',
  });
}
